import { CountrySelector } from '@/components/common/CountrySelector';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { format } from 'date-fns';
import { Trash2 } from 'lucide-react';
import type { Control } from 'react-hook-form';
import { FormFieldWrapper } from '../../FormFieldWrapper';
import type { FormKData } from '../types/formK.types';

interface ControllingOwnerCardProps {
  control: Control<FormKData>;
  index: number;
  onRemove?: () => void;
  canRemove: boolean;
}

/**
 * Controlling Owner Card Component
 * Reusable component for rendering a single controlling owner's form fields
 */
export function ControllingOwnerCard({
  control,
  index,
  onRemove,
  canRemove,
}: ControllingOwnerCardProps) {
  const { t: tForms } = useFormsTranslation();

  return (
    <div className='space-y-4 border rounded-lg p-4'>
      <div className='flex justify-between items-center mb-4'>
        <h5 className='text-sm font-medium'>
          {tForms('formK.labels.controllingOwnerNumber', {
            number: index + 1,
          })}
        </h5>
        {canRemove && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={onRemove}
            className='text-destructive hover:text-destructive'
          >
            <Trash2 className='w-4 h-4 mr-1' />
            {tForms('formK.buttons.remove')}
          </Button>
        )}
      </div>

      {/* Name Fields (2 columns) */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`controllingOwners.${index}.lastName`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('formK.fields.lastName')}
              name={`controllingOwners.${index}.lastName`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('formK.placeholders.lastName')}
                className='min-h-[44px]'
                autoComplete='family-name'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`controllingOwners.${index}.firstName`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('formK.fields.firstName')}
              name={`controllingOwners.${index}.firstName`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('formK.placeholders.firstName')}
                className='min-h-[44px]'
                autoComplete='given-name'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      {/* Address Fields (1 column) */}
      <div className='grid grid-cols-1 gap-4 items-start'>
        <FormField
          control={control}
          name={`controllingOwners.${index}.address`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('formK.fields.address')}
              name={`controllingOwners.${index}.address`}
              required
              error={fieldState.error?.message}
              tooltip={tForms('formK.tooltips.address')}
            >
              <Input
                {...field}
                placeholder={tForms('formK.placeholders.address')}
                className='min-h-[44px]'
                autoComplete='street-address'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`controllingOwners.${index}.postalCode`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('formK.fields.postalCode')}
              name={`controllingOwners.${index}.postalCode`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('formK.placeholders.postalCode')}
                className='min-h-[44px]'
                autoComplete='postal-code'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`controllingOwners.${index}.city`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('formK.fields.city')}
              name={`controllingOwners.${index}.city`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('formK.placeholders.city')}
                className='min-h-[44px]'
                autoComplete='address-level2'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      {/* Country and Nationality (2 columns) */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`controllingOwners.${index}.country`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('formK.fields.country')}
              name={`controllingOwners.${index}.country`}
              required
              error={fieldState.error?.message}
            >
              <CountrySelector
                value={field.value}
                onValueChange={field.onChange}
                placeholder={tForms('formK.placeholders.country')}
                error={fieldState.error?.message}
                required
                name={`controllingOwners.${index}.country`}
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`controllingOwners.${index}.nationality`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('formK.fields.nationality')}
              name={`controllingOwners.${index}.nationality`}
              required
              error={fieldState.error?.message}
            >
              <CountrySelector
                value={field.value}
                onValueChange={field.onChange}
                placeholder={tForms('formK.placeholders.nationality')}
                error={fieldState.error?.message}
                required
                name={`controllingOwners.${index}.nationality`}
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      {/* Birth Date */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`controllingOwners.${index}.birthDate`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('formK.fields.birthDate')}
              name={`controllingOwners.${index}.birthDate`}
              error={fieldState.error?.message}
            >
              <Input
                type='date'
                value={field.value ? format(field.value, 'yyyy-MM-dd') : ''}
                onChange={e => {
                  const date = e.target.value
                    ? new Date(e.target.value)
                    : undefined;
                  field.onChange(date);
                }}
                className='min-h-[44px]'
                max={format(new Date(), 'yyyy-MM-dd')} // Prevent future dates
                min='1900-01-01' // Reasonable minimum birth year
              />
            </FormFieldWrapper>
          )}
        />
      </div>
    </div>
  );
}
